import {CoreObjectArchivable} from 'app-core/platform/archivable/CoreObjectArchiveConstants';
import {
  ArchivableItemREST,
  ArchivableItemGQL,
  MaybeArchivableItem,
  isArchiveRecordGraphQL,
} from 'archive/ArchiveConstants';
import {assertAndContinue} from 'Asserts';
import {MakeFieldsRequired} from 'util/Types';

/**
 * Legacy REST implementation of `isArchived` and type predicate. Prefer use of `isArchived` directly.
 * @package
 */
export function isArchivedREST(item: MaybeArchivableItem): item is ArchivableItemREST {
  const archiveRecordValue = item.archiveRecord;

  // Some backbone models have archiveRecord as a method - call it to get the actual value
  if (typeof archiveRecordValue === 'function') {
    return Boolean((archiveRecordValue as () => unknown).call(item));
  }

  return Boolean(archiveRecordValue);
}

/**
 * Legacy GraphQL implementation of `isArchived`. Prefer use of `isArchived` directly.
 * @package
 */
export function isArchivedGQL(item: MaybeArchivableItem): item is ArchivableItemGQL {
  return isArchiveRecordGraphQL(item.archiveRecord);
}

/**
 * Modern Core Object implementation of `isArchived`. Prefer use of `isArchived` directly.
 * @package
 */
export function isCoreObjectArchived(item: MaybeArchivableItem): item is CoreObjectArchivable {
  return Boolean(item.archiveData?.archived);
}

export type ArchivedItem<T extends MaybeArchivableItem> = T extends ArchivableItemREST
  ? MakeFieldsRequired<T, 'archiveRecord'>
  : T extends ArchivableItemGQL
    ? MakeFieldsRequired<T, 'archiveRecord'>
    : T extends CoreObjectArchivable
      ? MakeFieldsRequired<T, 'archiveData'>
      : never;

/**
 * Preferred modern implementation of `isArchived` that can handle all types of archival:
 * - Legacy REST representation
 * - Legacy GraphQL (pre-Core Object) representation
 * - Modern Core Object GraphQL representation
 */
export default function isArchived<T extends MaybeArchivableItem>(item: T): item is ArchivedItem<T> {
  if (item == null) {
    assertAndContinue(false, 'isArchived called with null');
    return false;
  }
  return isCoreObjectArchived(item) || isArchivedREST(item) || isArchivedGQL(item);
}
